import React, { useEffect, useRef, useState, FC } from "react";
import Autocomplete, {
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import GenTextField, { GenTextFieldProps } from "../GenTextField/GenTextField";
import { useTranslation } from "react-i18next";

export interface GenAutoCompleteProps<T> {
  clearOnSelect?: boolean;
  disabled?: boolean;
  data: T[];
  value?: T | null;
  defaultValue?: T;
  open?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  label?: string;
  labelAttractor: (data: T) => string;
  filterOptions?: (data: T[], value?: string) => T[];
  onChange?: (value: T | Array<T> | null, e?: React.SyntheticEvent) => void;
  onOpen?: () => void;
  onRemoveOption?: () => void;
  onSelect?: (option: T | null) => void;
}

interface InnerTextFieldProps {
  params: Omit<GenTextFieldProps, "ref">;
  children?: any;
}

const InnerTextField: FC<InnerTextFieldProps> = (props) => {
  const { params } = props;
  return <GenTextField {...params} />;
};

export const GenAutoComplete = <T,>(props: GenAutoCompleteProps<T>) => {
  const {
    clearOnSelect,
    disabled,
    data,
    labelAttractor,
    value = null,
    defaultValue,
    open,
    required,
    error,
    helperText,
    filterOptions,
    onChange,
    onOpen,
    onSelect,
    onRemoveOption,
    label,
  } = props;
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<T | null>(value);
  const filterOptionsArray = useRef<T[] | null>(null);

  useEffect(() => {
    if (value === null) setSelectedValue(null);
  }, [value]);

  return (
    <>
      <Autocomplete<T>
        disabled={disabled}
        open={open}
        openOnFocus={false}
        autoSelect
        defaultValue={defaultValue}
        value={selectedValue}
        options={data}
        isOptionEqualToValue={(option: T, value: T) =>
          Object.keys(option as Record<string, any>).every(
            (key) =>
              (option as Record<string, any>)[key] ===
              (value as Record<string, any>)[key]
          )
        }
        // popupIcon={<SearchIcon color='primary' />}
        // sx={{'& .MuiAutocomplete-popupIndicatorOpen': {transform: 'none'}}}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <InnerTextField
            params={{
              ...params,
              label,
              required,
              error: error ? t(error) : undefined,
              helperText,
              InputLabelProps: { children: null },
            }}
          />
        )}
        filterOptions={
          filterOptions
            ? (data, state) => {
                const options = filterOptions(data, state.inputValue);
                filterOptionsArray.current = options;
                return options;
              }
            : undefined
        }
        getOptionLabel={labelAttractor}
        onChange={(e, value, reason) => {
          if (clearOnSelect) {
            setSelectedValue(null);
          } else {
            setSelectedValue(value);
          }
          if (onChange) onChange(value, e);
          if (reason === "removeOption" && onRemoveOption) onRemoveOption();
        }}
        onOpen={onOpen}
        onClose={(e, reason) => {
          if (reason === "selectOption" && onSelect) {
            onSelect(selectedValue);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Backspace" || e.key === "Delete") {
            e.nativeEvent.stopImmediatePropagation();
          }
        }}
      />
    </>
  );
};

export default GenAutoComplete;
